import React from 'react';
import type { PurchaseOrder } from '../../types';
import { previewPurchaseOrderReceive } from '../../lib/purchaseOrderReceive';
import type { InventoryItem } from '../../types';

interface ReceivePreviewModalProps {
  po: PurchaseOrder;
  inventory: InventoryItem[];
  supplierName: string;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

const actionLabel: Record<string, string> = {
  add_stock: 'Add to existing stock',
  match_existing: 'Match & add stock',
  create_new: 'Create new inventory item',
};

const ReceivePreviewModal: React.FC<ReceivePreviewModalProps> = ({
  po,
  inventory,
  supplierName,
  onConfirm,
  onCancel,
  loading,
}) => {
  const previews = previewPurchaseOrderReceive(po, inventory);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[85vh] w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="border-b px-6 py-4">
          <h3 className="text-lg font-bold text-gray-900">Receive {po.id}?</h3>
          <p className="text-sm text-gray-600">
            From <strong>{supplierName}</strong> — inventory will be updated.
          </p>
        </div>
        <ul className="max-h-64 overflow-y-auto divide-y px-6 py-2">
          {previews.map((p) => (
            <li key={p.lineId} className="py-2 text-sm">
              <div className="flex justify-between gap-2">
                <span className="font-medium text-gray-900">
                  {p.productName} × {p.quantity}
                </span>
                <span
                  className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
                    p.action === 'create_new'
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'bg-blue-100 text-blue-800'
                  }`}
                >
                  {actionLabel[p.action] ?? p.action}
                </span>
              </div>
              {p.matchedName ? (
                <p className="text-xs text-gray-500">{p.matchedName}</p>
              ) : null}
            </li>
          ))}
        </ul>
        <div className="flex justify-end gap-3 border-t bg-gray-50 px-6 py-4">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md bg-gray-200 px-4 py-2 text-gray-800 hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={loading}
            onClick={() => void onConfirm()}
            className="rounded-md bg-green-600 px-4 py-2 font-medium text-white hover:bg-green-700 disabled:opacity-50"
          >
            {loading ? 'Receiving…' : 'Confirm receive'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReceivePreviewModal;
